import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { DynamicLeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/DynamicLeverage.modal'
import { LeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/Leverage.modal'
import {
  ChangeLeverageModalType,
  TradingAccountsChangeLeverageModal,
} from '@pages/Traders-Room/Trading-Accounts/modals/TradingAccountsChangeLeverage.modal'

import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'

import { Pagination } from '@ui/v2/Pagination/Pagination'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { getScrollToCardId } from '@utils/getItemId'
import { CommonPaths } from '@utils/path-common'

import { LeverageTypeDto } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import {
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import { DemoTradingAccount, TradingAccountStatusType } from '@model/TradingAccount'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import { MobileDemoTradingAccountCard } from '../MobileDemoTradingAccountCard'
import { CreateTradingAccountCTA } from '../components/CreateTradingAccountCTA'
import { DemoTradingAccountsCards } from './DemoTradingAccountsCards'

export const DemoTradingAccountsPage: React.FC = () => {
  const { t } = useTranslation()
  const { product } = useProductReadContext()
  const isMobile = useWindowResize()

  const dynamicLeverageModal = useModal(DynamicLeverageModal)
  const leverageModal = useModal(LeverageModal)
  const { pageQuery, setPageQuery } = useOutletContext<TradingAccountsOutletCtx>()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const changeLeverageModal = useModal(TradingAccountsChangeLeverageModal)
  const changeLeverageSuccessModal = useModal(InformationModal, {
    title: t('Trading Account.Leverage changed'),
  })

  const searchKey = { pageQuery }

  const tableQuery = usePaginatedQuery({
    queryKey: queryKeys.tradingAccount.demo(searchKey),
    queryFn: (q) =>
      apiClient.getDemoTradingAccounts({
        ...q,
        ...pageQuery,
        search: `(Status == ${TradingAccountStatusType.Approved}) OR (Status == ${TradingAccountStatusType.CloseOnly})`,
      }),
  })
  const { forceRefresh } = tableQuery

  const { data: platformTypes = [] } = useQuery({
    queryKey: queryKeys.tradingAccount.platformTypes(product),
    queryFn: () => apiClient.getPlatformTypes(product),
  })
  const onlyCFDAccounts = tableQuery.data.filter(
    (acc) =>
      isMT4PlatformType(acc.platformType.id) ||
      isMT5PlatformType(acc.platformType.id) ||
      isTMTPlatformType(acc.platformType.id) ||
      isTradingViewPlatformType(acc.platformType.id)
  )

  const handleChangeLeverage = async (
    ta: DemoTradingAccount,
    oldLev: NameDto,
    newLev: LeverageTypeDto
  ) => {
    const modalData: ChangeLeverageModalType = {
      visible: true,
      previousValue: { id: oldLev.id, name: oldLev.name },
      newValue: { id: newLev.id, name: newLev.name },
      step: 1,
      data: ta,
      demo: true,
    }
    changeLeverageModal.open({
      data: modalData,
      onProceed: () => handleConfirmChangeLeverage(modalData),
    })
  }

  const handleConfirmChangeLeverage = async (data: ChangeLeverageModalType) => {
    try {
      if (data.demo && data.data && data.newValue) {
        await apiClient.updateDemoTradingAccount(data.data.id, {
          leverageTypeId: data.newValue.id,
          accountGroupId: data.data.accountGroup.id,
        })
        queryClient.invalidateQueries({ queryKey: ['tradingAccount'] })
        forceRefresh()
      }
      changeLeverageModal.close()
      changeLeverageSuccessModal.open({
        description: t('Trading Account.Leverage changed from to', {
          oldValue: data.previousValue?.name,
          newValue: data.newValue?.name,
        }),
      })
    } catch (error: unknown) {
      changeLeverageSuccessModal.close()
      console.error(error, 'error')
    }
  }

  const onLeverageClick = (args: { tradingAccount: DemoTradingAccount }) => {
    const tradingAccount = args.tradingAccount
    if (
      isTMTPlatformType(tradingAccount?.platformType.id) ||
      isTMTPlatformType(tradingAccount?.parentPlatformType?.id)
    ) {
      return dynamicLeverageModal.open()
    }
    leverageModal.open({
      platformTypeId: tradingAccount.platformType.id,
      currentLeverageId: tradingAccount.leverage.id,
      restrictions: [],
      onSelect: (selectedLeverage) =>
        handleChangeLeverage(tradingAccount, tradingAccount.leverage, selectedLeverage),
    })
  }

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    const newPageQuery = {
      ...pageQuery,
      pageIndex,
      pageSize,
    }
    setPageQuery?.(newPageQuery)
    tableQuery.setPageQuery?.(newPageQuery)
  }

  const Content = () =>
    isMobile ? (
      !onlyCFDAccounts.length && !tableQuery.isLoading ? (
        <CreateTradingAccountCTA createTradingAccountPath={CommonPaths.NewDemoTradingAccount} />
      ) : (
        onlyCFDAccounts.map((tradingAccount, index) => (
          <MobileDemoTradingAccountCard
            tradingAccount={tradingAccount}
            platformTypes={platformTypes}
            onDisplayDynamicLeverageInfo={() => dynamicLeverageModal.open()}
            cardId={getScrollToCardId(index, onlyCFDAccounts.length, 'demo-trading-accounts')}
            key={tradingAccount.id}
            onLeverageChange={onLeverageClick}
          />
        ))
      )
    ) : (
      <DemoTradingAccountsCards
        tableQuery={{
          ...tableQuery,
          data: onlyCFDAccounts,
        }}
        pageQuery={tableQuery.pageQuery}
        setPageQuery={tableQuery.setPageQuery}
        platformTypes={platformTypes}
        onLeverageChange={onLeverageClick}
        onDisplayDynamicLeverageInfo={() => dynamicLeverageModal.open()}
      />
    )

  return (
    <Loading isLoading={tableQuery.isLoading}>
      <Content />
      {tableQuery.meta && tableQuery.hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.DemoTradingAccountsHeader}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </Loading>
  )
}
